import { useEffect } from 'react'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  // Set false for a required gate the user must complete via an action
  // inside the modal (e.g. accepting terms) rather than dismiss.
  dismissible?: boolean
  // 'lg' widens the modal for content that needs more room (e.g. a
  // multi-column pricing table) — default fits the usual single-form case.
  size?: 'md' | 'lg'
  // Rendered in the header next to (or, when dismissible=false, in place of)
  // the close button. Non-dismissible modals fully cover the sidebar
  // (z-50 over z-40), so an onboarding gate with no other way out — e.g.
  // App.tsx's terms/plan/merchant-setup modals — needs its own escape hatch
  // such as a "Log out" action here, or the user is stuck with no way to
  // sign out until they complete the gate.
  headerAction?: React.ReactNode
}

export const Modal: React.FC<ModalProps> = ({ open, onClose, title, children, dismissible = true, size = 'md', headerAction }) => {
  useEffect(() => {
    if (!open || !dismissible) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onClose, dismissible])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={dismissible ? onClose : undefined} />
      <div className={`relative bg-white rounded-lg shadow-xl w-full ${size === 'lg' ? 'max-w-4xl' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-lg">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <div className="flex items-center gap-4">
            {headerAction}
            {dismissible && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  )
}
